import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircuitBoard,
  ShieldCheck,
  Wrench,
  Zap,
  Building2,
  Factory,
  Monitor,
  CheckCircle2,
} from "lucide-react";
import { COMPANY, IMG, VID } from "@/lib/constants";
import { CTASection } from "@/components/CTASection";
import { SectionHeading, EyebrowLabel } from "@/components/SectionHeading";

const heroImage = IMG.heroBreakers;

const industryImages = {
  "Manufacturing Plants": IMG.factoryFloor,
  "IT Companies": IMG.controlRoom,
  "Commercial Buildings": IMG.switchgear,
};

const heroPoints = [
  "Complete Electrical Panel Solutions",
  "Design, Manufacturing & Maintenance",
  "Complete Installation & Testing",
];

const stats = [
  { value: "25+", label: "Real-Time Projects" },
  { value: "100%", label: "Quality Assured" },
  { value: "24h", label: "Response Time" },
  { value: "5+", label: "Panel Categories" },
];

const capabilities = [
  {
    icon: CircuitBoard,
    title: "Panel Design",
    desc: "SLD, Layout & Control Circuits engineered on AutoCAD to industry standards.",
    color: "blue",
    colorHex: "#3B82F6",
    bgLight: "#EFF6FF",
  },
  {
    icon: Wrench,
    title: "Manufacturing",
    desc: "MCC, PCC, PMCC and APFC panels built with premium components & workmanship.",
    color: "emerald",
    colorHex: "#10B981",
    bgLight: "#ECFDF5",
  },
  {
    icon: ShieldCheck,
    title: "Installation & Testing",
    desc: "On-site commissioning, load testing and compliance validation.",
    color: "violet",
    colorHex: "#8B5CF6",
    bgLight: "#F5F3FF",
  },
  {
    icon: Zap,
    title: "Maintenance",
    desc: "Preventive maintenance, fault analysis and retrofit upgrades.",
    color: "amber",
    colorHex: "#F59E0B",
    bgLight: "#FFFBEB",
  },
];

const industries = [
  { icon: Factory, name: "Manufacturing Plants" },
  { icon: Monitor, name: "IT Companies" },
  { icon: Building2, name: "Commercial Buildings" },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO — full viewport */}
      <section
        data-testid="hero-section"
        className="relative h-screen min-h-[640px] flex items-center overflow-hidden bg-navy-950 text-white"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${heroImage}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/85 to-navy-950/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(234,179,8,0.18),transparent_55%)]" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-12 w-full pt-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* LEFT — main content */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-[#EAB308]">
                <span className="h-px w-8 bg-[#EAB308]" />
                Professional Electrical Engineers
              </div>

              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                Powering <span className="text-[#EAB308]">Industries</span>{" "}
                <br className="hidden sm:inline" />
                With Precision.
              </h1>

              <p className="mt-5 max-w-xl text-zinc-300 leading-relaxed">
                Complete Electrical Panel Solutions — Design, Manufacturing,
                Installation & Maintenance for manufacturing plants, IT
                companies and commercial buildings.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 bg-[#EAB308] hover:bg-[#FACC15] text-zinc-950 px-6 py-3 font-bold uppercase tracking-wide text-xs sm:text-sm border-2 border-[#EAB308]"
                >
                  Discuss Your Panel Requirement
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-white hover:text-zinc-950 text-white px-6 py-3 font-bold uppercase tracking-wide text-xs sm:text-sm border-2 border-white"
                >
                  Explore Services
                </Link>
              </div>



              {/* MOBILE stats strip (since right card is hidden on mobile) */}
              <div className="mt-8 lg:hidden grid grid-cols-3 border border-zinc-800 divide-x divide-zinc-800 bg-zinc-900/60 backdrop-blur">
                {stats.slice(0, 3).map((s) => (
                  <div key={s.label} className="px-2 py-3 text-center">
                    <div className="text-lg font-black tracking-tighter text-[#EAB308]">
                      {s.value}
                    </div>
                    <div className="mt-0.5 text-xs uppercase tracking-[0.2em] text-zinc-400 font-bold">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — stacked key points card (fills the void) */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute -top-3 -left-3 h-20 w-20 border-2 border-[#EAB308] z-0" />
                <div className="absolute -bottom-3 -right-3 h-20 w-20 bg-[#EAB308]/90 z-0" />

                <div className="relative bg-navy-900/80 backdrop-blur-xl border border-navy-800">>
                  <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <div className="text-xs font-black uppercase tracking-[0.3em] text-[#EAB308]">
                      What We Deliver
                    </div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                      Est. 25+ Projects
                    </div>
                  </div>

                  <ul className="divide-y divide-zinc-800">
                    {heroPoints.map((p, i) => (
                      <li
                        key={p}
                        className="flex items-center gap-4 px-6 py-4 hover:bg-zinc-800/40 transition-colors"
                      >
                        <span className="inline-flex h-9 w-9 items-center justify-center bg-[#EAB308] text-zinc-950 font-black text-sm">
                          0{i + 1}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-white leading-tight">
                            {p}
                          </div>
                        </div>
                        <CheckCircle2 className="h-4 w-4 text-[#EAB308] ml-auto shrink-0" />
                      </li>
                    ))}
                  </ul>

                  <div className="grid grid-cols-3 divide-x divide-zinc-800 border-t border-zinc-800">
                    {stats.slice(0, 3).map((s) => (
                      <div key={s.label} className="px-3 py-4 text-center">
                        <div className="text-xl font-black tracking-tighter text-[#EAB308]">
                          {s.value}
                        </div>
                        <div className="mt-0.5 text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* scroll hint */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-xs uppercase tracking-[0.3em] text-zinc-500 font-bold">
          <span>Scroll</span>
          <span className="h-6 w-px bg-gradient-to-b from-[#EAB308] to-transparent" />
        </div>
      </section>

      {/* WELCOME */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
            <div className="relative group w-full">
              <div className="absolute -top-6 -left-6 h-24 w-24 border-4 border-[#EAB308] z-0 opacity-20 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute -bottom-6 -right-6 h-24 w-24 bg-[#EAB308]/10 z-0" />
              
              <div className="relative overflow-hidden border-2 border-navy-900 bg-navy-950 shadow-[20px_20px_60px_-15px_rgba(0,0,0,0.3)]">
                <video
                  src={VID.welcome}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-auto cursor-pointer scale-[1.005]"
                  onClick={(e) => {
                    if (e.target.paused) e.target.play();
                    else e.target.pause();
                  }}
                />
              </div>
            </div>

            <div>
              <p className="text-lg text-zinc-700 leading-relaxed">
                We provide complete Electrical Panel Solutions including Design,
                Manufacturing, Installation, and Maintenance for industries like
                manufacturing plants, IT companies, and commercial buildings.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-0 border border-zinc-200">
                {[
                  { k: "25+", v: "Real-Time Projects" },
                  { k: "⚡", v: "Experienced Engineering Team" },
                  { k: "A+", v: "Top-Quality Panels & Service" },
                ].map((h, i) => (
                  <div
                    key={h.v}
                    className={`p-7 ${
                      i !== 2 ? "sm:border-r border-zinc-200" : ""
                    } border-b sm:border-b-0 border-zinc-200`}
                  >
                    <div className="text-4xl font-black tracking-tighter text-zinc-950">
                      {h.k}
                    </div>
                    <div className="mt-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                      {h.v}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link
                  to="/contact"
                  data-testid="welcome-cta"
                  className="inline-flex items-center gap-2 bg-navy-950 hover:bg-[#EAB308] hover:text-navy-950 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm border-2 border-navy-950"
                >
                  Discuss Your Panel Requirements
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-zinc-50 border-y border-zinc-200 relative overflow-hidden">
        {/* Subtle grid pattern background */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" 
             style={{ backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`, backgroundSize: '30px 30px' }} />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-28">
          <SectionHeading
            eyebrow="Capabilities"
            title="Complete panel solutions, end-to-end."
            subtitle="From the first SLD to on-site commissioning — we own every step with engineering rigor."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card 1: Wide Dark Panel */}
            <div className="md:col-span-2 rounded-[2rem] p-8 lg:p-12 min-h-[300px] lg:min-h-[320px] flex flex-col justify-between relative overflow-hidden group border border-zinc-800 shadow-md">
              <img src={IMG.engineerTablet} alt="Panel Design" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[3000ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-navy-950/85 mix-blend-multiply opacity-100 group-hover:opacity-90 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
              <div 
                className="absolute inset-0 opacity-[0.08]" 
                style={{ 
                  backgroundImage: `linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)`, 
                  backgroundSize: '30px 30px' 
                }} 
              />
              <div className="relative z-10 flex items-center justify-between">
                <div className="inline-flex items-center justify-center p-3 rounded-xl bg-white/10 backdrop-blur-md text-[#EAB308] border border-white/20">
                  <CircuitBoard className="h-8 w-8" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white/60 bg-navy-900/30 px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10">
                  01 // Capability
                </span>
              </div>
              <div className="relative z-10 mt-12 md:mt-0">
                <h3 className="text-3xl lg:text-4xl font-black tracking-tighter text-white">
                  Panel Design
                </h3>
                <p className="mt-4 text-zinc-300 leading-relaxed max-w-md">
                  SLD, Layout & Control Circuits engineered on AutoCAD to industry standards.
                </p>
              </div>
            </div>

            {/* Card 2: Square Emerald Card */}
            <div className="col-span-1 rounded-[2rem] p-8 lg:p-12 min-h-[300px] lg:min-h-[320px] flex flex-col justify-between relative overflow-hidden group shadow-xl border border-emerald-500/20">
              <img src={IMG.workshopA} alt="Manufacturing" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[3000ms] group-hover:scale-110" />
              {/* Refined overlays for better depth and richness */}
              <div className="absolute inset-0 bg-emerald-950/80 group-hover:bg-emerald-950/70 transition-colors duration-700" />
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/60 via-transparent to-black/90" />
              <div className="relative z-10 flex items-start justify-between">
                <div className="inline-flex items-center justify-center p-3 rounded-xl bg-white/10 backdrop-blur-md text-emerald-400 border border-white/10 group-hover:border-emerald-500/50 transition-colors">
                  <Wrench className="h-8 w-8" strokeWidth={1.5} />
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-200/60 bg-navy-900/40 px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10">02</span>
                  <div className="h-1 w-8 bg-emerald-500/50 rounded-full" />
                </div>
              </div>
              <div className="relative z-10 mt-12 md:mt-0">
                <h3 className="text-2xl lg:text-3xl font-black tracking-tight text-white drop-shadow-lg">
                  Manufacturing
                </h3>
                <p className="mt-4 text-emerald-50/90 leading-relaxed text-sm font-medium">
                  MCC, PCC, PMCC and APFC panels built with premium components & workmanship.
                </p>
                <div className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 duration-500">
                  Precision Engineering <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </div>

            {/* Card 3: Square Yellow Card */}
            <div className="col-span-1 rounded-[2rem] p-8 lg:p-12 min-h-[300px] lg:min-h-[320px] flex flex-col justify-between relative overflow-hidden group shadow-[0_20px_40px_-15px_rgba(234,179,8,0.4)] border border-amber-400">
              <img src={IMG.multimeter} alt="Installation" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[3000ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-[#EAB308]/90 backdrop-blur-[2px] opacity-100 group-hover:opacity-95 transition-opacity duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#ca8a04]/40 to-transparent" />
              <div className="relative z-10 flex items-start justify-between">
                <div className="inline-flex items-center justify-center p-3 rounded-xl bg-navy-950 text-[#EAB308] border border-navy-800">
                  <ShieldCheck className="h-8 w-8" strokeWidth={1.5} />
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-navy-900 bg-navy-900/10 px-3 py-1.5 rounded-full backdrop-blur-sm border border-navy-800/20">03</span>
              </div>
              <div className="relative z-10 mt-12 md:mt-0">
                <h3 className="text-2xl font-black tracking-tight text-zinc-950">
                  Installation
                </h3>
                <p className="mt-4 text-zinc-900 leading-relaxed text-sm font-semibold">
                  On-site commissioning, load testing and compliance validation across sites.
                </p>
              </div>
            </div>

            {/* Card 4: Wide Light Frosted Card */}
            <div className="md:col-span-2 rounded-[2rem] p-8 lg:p-12 min-h-[300px] lg:min-h-[320px] flex flex-col justify-between relative overflow-hidden group shadow-sm border border-zinc-200">
               <img 
                 src={IMG.industrialPanel} 
                 className="absolute inset-0 w-full h-full object-cover grayscale transition-transform duration-[3000ms] group-hover:scale-105" 
                 alt="Maintenance" 
               />
               <div className="absolute inset-0 bg-white/85 backdrop-blur-sm opacity-100 group-hover:bg-white/90 transition-colors duration-700" />
               <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent" />
               <div className="relative z-10 flex items-start justify-between">
                 <div className="inline-flex items-center justify-center p-3 rounded-xl bg-amber-100/90 backdrop-blur-md text-amber-600 border border-amber-200">
                   <Zap className="h-8 w-8" strokeWidth={1.5} />
                 </div>
                 <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 bg-white/50 px-3 py-1.5 rounded-full backdrop-blur-sm border border-zinc-200">
                    04 // Capability
                 </span>
               </div>
               <div className="relative z-10 mt-12 md:mt-0 lg:w-2/3">
                 <h3 className="text-3xl lg:text-4xl font-black tracking-tighter text-zinc-950">
                   Maintenance
                 </h3>
                 <p className="mt-4 text-zinc-700 font-medium leading-relaxed">
                   Preventive maintenance, fault analysis and retrofit upgrades. We ensure uninterrupted power workflows indefinitely with expert support.
                 </p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <SectionHeading
              eyebrow="Industries Served"
              title="Trusted across critical sectors."
            />
            <Link
              to="/services"
              data-testid="industries-cta"
              className="inline-flex self-start items-center gap-2 text-sm font-bold uppercase tracking-wide text-zinc-950 hover:text-[#CA8A04] border-b-2 border-zinc-950 hover:border-[#CA8A04] pb-1"
            >
              View All Services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-16 flex flex-col lg:grid lg:grid-cols-3 gap-8 pb-4 lg:pb-0 relative">
            {industries.map((ind, i) => (
              <div
                key={ind.name}
                className={`group flex flex-col bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:border-zinc-300 transition-colors
                  sticky lg:static shadow-xl lg:shadow-sm 
                  ${i === 0 ? "top-24 z-10" : ""}
                  ${i === 1 ? "top-[7rem] z-20" : ""}
                  ${i === 2 ? "top-[8rem] z-30" : ""}
                `}
              >
                <div className="relative h-64 overflow-hidden bg-zinc-100 p-0">
                  <img
                    src={industryImages[ind.name]}
                    alt={ind.name}
                    className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute top-4 left-4 bg-[#EAB308] text-zinc-950 p-2 shadow-sm">
                     <ind.icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                </div>

                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-3">
                     <span className="h-px w-6 bg-[#EAB308]" />
                     <span className="text-xs font-black uppercase tracking-[0.3em] text-zinc-500">
                       Sector
                     </span>
                  </div>
                  
                  <h3 className="mt-4 text-2xl font-black tracking-tight text-zinc-950">
                    {ind.name}
                  </h3>
                  
                  <p className="mt-4 text-sm text-zinc-600 leading-relaxed">
                    Delivering mission-critical electrical solutions optimized for {ind.name.toLowerCase()} environments.
                  </p>
                  
                  <div className="mt-8 pt-6 border-t border-zinc-100 mt-auto flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-950">
                    Read More 
                    <ArrowRight className="h-4 w-4 text-[#EAB308]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
